// Tab "Configurações" (§6.2) — wave 2 EDITÁVEL: form RHF+zod das
// ~30 configurações de negócio em accordion de seções (Assinatura,
// E-mail, Checkout, Boleto, Transação, Refund retry), com um PUT
// ÚNICO no rodapé (dirty-state Descartar/Salvar). GET 404 = white
// label sem configuração → form com defaults sensatos e o PUT cria
// (upsert). Campos declarados por seção (kind: switch/int/text).
import { zodResolver } from "@hookform/resolvers/zod"
import { InfoIcon } from "lucide-react"
import { useEffect, useMemo } from "react"
import type { FieldPath } from "react-hook-form"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  useAdminWhiteLabelConfigurations,
  useUpsertAdminWhiteLabelConfigurationsMutation,
} from "@/services/admin"

import {
  buildConfigurationsFormSchema,
  configurationsFormDefaults,
  type ConfigurationsFormValues,
  toConfigurationsPayload,
} from "../schemas"
import { FieldInfo } from "./field-info"

const panelPrefix = "pages.adminWhiteLabels"

// Tipo de render de cada campo do form.
type FieldKind = "switch" | "int" | "text"

interface SectionField {
  name: FieldPath<ConfigurationsFormValues>
  // Chave do label em configurations.fields.* (os campos do refund
  // retry reusam as chaves planas legadas refundRetry*).
  labelKey: string
  kind: FieldKind
}

// Seções na ordem do wireframe §6.2, espelhando o agrupamento do
// AdminWhiteLabelConfigurationUpdateRequest.
const SECTIONS: Array<{
  key: string
  fields: SectionField[]
}> = [
  {
    key: "subscription",
    fields: [
      {
        name: "subscriptionTotalRetry",
        labelKey: "subscriptionTotalRetry",
        kind: "int",
      },
      {
        name: "cancelAtLastRetry",
        labelKey: "cancelAtLastRetry",
        kind: "switch",
      },
      {
        name: "expireFirstCycle",
        labelKey: "expireFirstCycle",
        kind: "switch",
      },
      {
        name: "subscriptionAutoExpirationEnabled",
        labelKey: "subscriptionAutoExpirationEnabled",
        kind: "switch",
      },
      {
        name: "cycleAutoExpirationEnabled",
        labelKey: "cycleAutoExpirationEnabled",
        kind: "switch",
      },
      {
        name: "insuranceExpirationDays",
        labelKey: "insuranceExpirationDays",
        kind: "int",
      },
    ],
  },
  {
    key: "email",
    fields: [
      {
        name: "sendMailByRejectedTransaction",
        labelKey: "sendMailByRejectedTransaction",
        kind: "switch",
      },
      {
        name: "showEmailParcelInfo",
        labelKey: "showEmailParcelInfo",
        kind: "switch",
      },
      {
        name: "emailSubjectPrefix",
        labelKey: "emailSubjectPrefix",
        kind: "text",
      },
    ],
  },
  {
    key: "checkout",
    fields: [
      {
        name: "newCheckoutEnabled",
        labelKey: "newCheckoutEnabled",
        kind: "switch",
      },
      {
        name: "checkoutCustom",
        labelKey: "checkoutCustom",
        kind: "switch",
      },
      {
        name: "checkoutCustomHost",
        labelKey: "checkoutCustomHost",
        kind: "text",
      },
      {
        name: "checkoutDueDateMinOffsetDays",
        labelKey: "checkoutDueDateMinOffsetDays",
        kind: "int",
      },
      {
        name: "checkoutDueDateMaxOffsetDays",
        labelKey: "checkoutDueDateMaxOffsetDays",
        kind: "int",
      },
      {
        name: "bypassLegalAge",
        labelKey: "bypassLegalAge",
        kind: "int",
      },
      {
        name: "requireSecurityCode",
        labelKey: "requireSecurityCode",
        kind: "switch",
      },
      {
        name: "requireBirthDate",
        labelKey: "requireBirthDate",
        kind: "switch",
      },
      {
        name: "restrictPaymentMethodChange",
        labelKey: "restrictPaymentMethodChange",
        kind: "switch",
      },
    ],
  },
  {
    key: "bankSlip",
    fields: [
      {
        name: "enableBankSlipPdfPassword",
        labelKey: "enableBankSlipPdfPassword",
        kind: "switch",
      },
      {
        name: "bankSlipInstructions",
        labelKey: "bankSlipInstructions",
        kind: "text",
      },
      {
        name: "daysPastDue",
        labelKey: "daysPastDue",
        kind: "int",
      },
    ],
  },
  {
    key: "transaction",
    fields: [
      {
        name: "softDescriptorPrefix",
        labelKey: "softDescriptorPrefix",
        kind: "text",
      },
      {
        name: "softDescriptorPattern",
        labelKey: "softDescriptorPattern",
        kind: "text",
      },
      {
        name: "usePay2bTransactionError",
        labelKey: "usePay2bTransactionError",
        kind: "switch",
      },
      {
        name: "orderCodePrefix",
        labelKey: "orderCodePrefix",
        kind: "text",
      },
      {
        name: "orderSubscriptionCodePrefix",
        labelKey: "orderSubscriptionCodePrefix",
        kind: "text",
      },
      {
        name: "sendCallbackInformation",
        labelKey: "sendCallbackInformation",
        kind: "switch",
      },
      {
        name: "smartRouting",
        labelKey: "smartRouting",
        kind: "switch",
      },
    ],
  },
  {
    key: "refundRetry",
    fields: [
      {
        name: "orderRefundRetryConfiguration.enabled",
        labelKey: "refundRetryEnabled",
        kind: "switch",
      },
      {
        name: "orderRefundRetryConfiguration.maxAttempts",
        labelKey: "refundRetryMaxAttempts",
        kind: "int",
      },
      {
        name: "orderRefundRetryConfiguration.intervalDays",
        labelKey: "refundRetryIntervalDays",
        kind: "int",
      },
      {
        name: "orderRefundRetryConfiguration.codes",
        labelKey: "refundRetryCodes",
        kind: "text",
      },
    ],
  },
]

// Campo genérico do form (switch / inteiro / texto).
const ConfigurationField = ({
  field: { name, labelKey, kind },
}: {
  field: SectionField
}) => {
  const { t } = useTranslation()
  const label = t(
    `${panelPrefix}.configurations.fields.${labelKey}`
  )

  return (
    <FormField
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between gap-4 py-1">
            <div className="flex flex-1 items-center gap-1.5">
              <FormLabel className="font-normal">
                {label}
              </FormLabel>
              <FieldInfo
                hintKey={`${panelPrefix}.configurations.descriptions.${labelKey}`}
              />
            </div>
            <FormControl>
              {kind === "switch" ? (
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                  aria-label={label}
                />
              ) : (
                <Input
                  className={
                    kind === "int"
                      ? "w-28 text-right tabular-nums"
                      : "w-64"
                  }
                  type={kind === "int" ? "number" : "text"}
                  min={kind === "int" ? 0 : undefined}
                  {...field}
                  value={String(field.value ?? "")}
                />
              )}
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

type ConfigurationsTabProps = {
  uuid: string
}

export const ConfigurationsTab = ({
  uuid,
}: ConfigurationsTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useAdminWhiteLabelConfigurations(uuid)
  const upsertMutation =
    useUpsertAdminWhiteLabelConfigurationsMutation(uuid)

  const schema = useMemo(
    () =>
      buildConfigurationsFormSchema({
        required: t(`${panelPrefix}.form.required`),
        invalidNumber: t(
          `${panelPrefix}.form.invalidNumber`
        ),
      }),
    [t]
  )

  const form = useForm<ConfigurationsFormValues>({
    resolver: zodResolver(schema),
    defaultValues: configurationsFormDefaults(data),
  })

  // O dado chega DEPOIS do mount (query) → repovoa o form limpo.
  // `data` null (404) mantém os defaults sensatos do form em branco.
  const { reset, formState } = form
  useEffect(() => {
    if (!formState.isDirty) {
      reset(configurationsFormDefaults(data))
    }
    // reset ao chegar dado novo; isDirty é lido do snapshot atual.
  }, [data, reset, formState.isDirty])

  if (isLoading) {
    return (
      <div
        data-testid="wl-configurations-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={`wl-config-skeleton-${index}`}
            className="h-14 w-full rounded-xl"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.configurations.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  const handleSubmit = form.handleSubmit((values) => {
    upsertMutation.mutate(toConfigurationsPayload(values), {
      onSuccess: () => {
        // Consolida o form no estado salvo (limpa o dirty).
        form.reset(values)
      },
    })
  })

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        noValidate
      >
        {/* 404 = ainda não configurado; salvar cria (upsert). */}
        {data === null && (
          <div
            data-testid="wl-configurations-not-configured"
            className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4"
          >
            <InfoIcon
              className="mt-0.5 size-4 shrink-0 text-blue-600"
              aria-hidden="true"
            />
            <p className="text-sm text-blue-800">
              {t(
                `${panelPrefix}.configurations.notConfigured`
              )}
            </p>
          </div>
        )}

        <Accordion
          type="multiple"
          defaultValue={SECTIONS.map(
            (section) => section.key
          )}
          className="space-y-2"
        >
          {SECTIONS.map((section) => (
            <AccordionItem
              key={section.key}
              value={section.key}
              className="rounded-xl border bg-card px-4 shadow-sm"
            >
              <AccordionTrigger className="hover:no-underline">
                <span className="text-sm font-semibold">
                  {t(
                    `${panelPrefix}.configurations.sections.${section.key}`
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-1 divide-y">
                {section.fields.map((field) => (
                  <ConfigurationField
                    key={field.name}
                    field={field}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Um PUT único no rodapé (dirty-state). */}
        {form.formState.isDirty && (
          <div
            data-testid="configurations-dirty-footer"
            className="sticky bottom-0 flex items-center justify-end gap-2 rounded-xl border bg-card p-3 shadow-sm"
          >
            <Button
              type="button"
              variant="outline"
              disabled={upsertMutation.isPending}
              onClick={() =>
                form.reset(configurationsFormDefaults(data))
              }
            >
              {t(`${panelPrefix}.form.discard`)}
            </Button>
            <Button
              type="submit"
              disabled={upsertMutation.isPending}
            >
              {t(`${panelPrefix}.form.save`)}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
